import apiClient from "@/services/api-client";

// ── Types ──────────────────────────────────────────────────

export interface HealthGridTenant {
  id: string;
  name: string;
  email: string;
  status: string;
  healthScore: number | null;
  riskLevel: "healthy" | "monitor" | "at_risk" | "critical" | "unscored";
  lastActiveAt: string | null;
  lifecycleStage: string | null;
  planName: string;
  planType: string | null;
  userCount: number;
  storeCount: number;
  onlineUsers: number;
  createdAt: string;
}

export interface HealthGridResponse {
  data: HealthGridTenant[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RiskSummary {
  healthy: number;
  monitor: number;
  at_risk: number;
  critical: number;
  unscored: number;
}

export interface TenantHealthDetail {
  id: string;
  tenantId: string;
  computedAt: string;
  healthScore: number;
  riskLevel: string;
  previousScore: number | null;
  scoreDelta: number | null;
  loginScore: number;
  featureScore: number;
  transactionScore: number;
  errorScore: number;
  syncScore: number;
  billingScore: number;
  storageScore: number;
  supportScore: number;
  uptimeScore: number;
  factors: Record<string, unknown>;
  recommendations: string[];
}

export interface PresenceStats {
  totalActiveTenants: number;
  tenantsOnline: number;
  tenantsOffline: number;
  totalConnections: number;
  byPlatform: Record<string, number>;
}

export interface LivePresenceEntry {
  tenantId: string;
  userId: string;
  deviceId: string;
  platform: string;
  appVersion?: string | null;
  lastHeartbeat: string;
  syncPendingChanges: number;
  appState?: {
    currentPage?: string;
    activeModule?: string;
    openSince?: string;
  };
}

export interface OnlineConnection {
  tenantId: string;
  userId: string;
  storeId?: string | null;
  deviceId: string;
  platform: string;
  appVersion?: string | null;
  ipAddress?: string | null;
  status: string;
  lastHeartbeat: string;
  systemInfo?: Record<string, unknown> | null;
  syncPendingChanges: number;
  appState?: {
    currentPage?: string;
    activeModule?: string;
    openSince?: string;
  };
}

export interface StorageUsage {
  id: string;
  tenantId: string;
  computedAt: string;
  productCount: number;
  batchCount: number;
  invoiceCount: number;
  customerCount: number;
  purchaseOrderCount: number;
  auditLogCount: number;
  syncEventCount: number;
  userCount: number;
  storeCount: number;
  estimatedDbSizeMB: number;
  fileStorageMB: number;
  totalStorageMB: number;
  quotaLimitMB: number;
  utilizationPercent: number;
  dailyGrowthMB: number | null;
  daysUntilFull: number | null;
}

export interface DailyMetrics {
  id: string;
  tenantId: string;
  date: string;
  totalUsers: number;
  activeUsers: number;
  totalLogins: number;
  uniqueLogins: number;
  totalInvoices: number;
  invoiceAmount: number;
  totalProducts: number;
  newProducts: number;
  totalCustomers: number;
  newCustomers: number;
  purchaseOrders: number;
  syncEvents: number;
  syncErrors: number;
  telemetryEvents: number;
  errorRate: number;
  avgResponseTime: number | null;
  topFeatures: Record<string, number> | null;
}

export interface ErrorGroup {
  id: string;
  tenantId: string;
  fingerprint: string;
  errorType: string;
  message: string;
  source: string | null;
  platform: string;
  stackTrace: string | null;
  occurrenceCount: number;
  affectedUsers: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolvedNote: string | null;
  assignedTo: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ErrorSummary {
  open: number;
  resolved: number;
  regressed: number;
  today: number;
  topTypes: Array<{ errorType: string; _count: { _all: number } }>;
}

export interface ErrorTrendBucket {
  date: string;
  total: number;
  new: number;
}

// ── Service ────────────────────────────────────────────────

export const monitoringService = {
  // Health Grid
  getHealthGrid: async (params?: {
    riskLevel?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<HealthGridResponse> => {
    const response = await apiClient.get<HealthGridResponse>(
      "/monitoring/health-grid",
      { params },
    );
    return response.data;
  },

  getRiskSummary: async (): Promise<RiskSummary> => {
    const response = await apiClient.get<RiskSummary>(
      "/monitoring/health/summary",
    );
    return response.data;
  },

  // Tenant Health Detail
  getTenantHealth: async (tenantId: string): Promise<TenantHealthDetail> => {
    const response = await apiClient.get<TenantHealthDetail>(
      `/monitoring/health/${tenantId}`,
    );
    return response.data;
  },

  getTenantHealthHistory: async (
    tenantId: string,
    days = 30,
  ): Promise<TenantHealthDetail[]> => {
    const response = await apiClient.get<TenantHealthDetail[]>(
      `/monitoring/health/${tenantId}/history`,
      { params: { days } },
    );
    return response.data;
  },

  // Presence
  getPresenceStats: async (): Promise<PresenceStats> => {
    const response = await apiClient.get<PresenceStats>(
      "/monitoring/presence/stats",
    );
    return response.data;
  },

  getLivePresence: async (): Promise<LivePresenceEntry[]> => {
    const response = await apiClient.get<LivePresenceEntry[]>(
      "/monitoring/presence/live",
    );
    return response.data;
  },

  getOnlineConnections: async (params?: {
    tenantId?: string;
    platform?: string;
  }): Promise<OnlineConnection[]> => {
    const response = await apiClient.get<OnlineConnection[]>(
      "/monitoring/presence",
      { params },
    );
    return response.data;
  },

  // Storage
  getTenantStorage: async (tenantId: string): Promise<StorageUsage> => {
    const response = await apiClient.get<StorageUsage>(
      `/monitoring/storage/${tenantId}`,
    );
    return response.data;
  },

  getTenantStorageHistory: async (
    tenantId: string,
    days = 30,
  ): Promise<StorageUsage[]> => {
    const response = await apiClient.get<StorageUsage[]>(
      `/monitoring/storage/${tenantId}/history`,
      { params: { days } },
    );
    return response.data;
  },

  // Daily Metrics
  getTenantMetrics: async (
    tenantId: string,
    params?: { startDate?: string; endDate?: string },
  ): Promise<DailyMetrics[]> => {
    const response = await apiClient.get<DailyMetrics[]>(
      `/monitoring/metrics/${tenantId}`,
      { params },
    );
    return response.data;
  },

  getTenantMetricsLatest: async (
    tenantId: string,
  ): Promise<DailyMetrics | null> => {
    const response = await apiClient.get<DailyMetrics | null>(
      `/monitoring/metrics/${tenantId}/latest`,
    );
    return response.data;
  },

  // Client Errors
  getTenantErrors: async (
    tenantId: string,
    params?: {
      status?: string;
      platform?: string;
      errorType?: string;
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    },
  ): Promise<{ data: ErrorGroup[]; total: number }> => {
    const response = await apiClient.get<{ data: ErrorGroup[]; total: number }>(
      "/errors",
      { params: { tenantId, ...params } },
    );
    return response.data;
  },

  getTenantErrorSummary: async (tenantId: string): Promise<ErrorSummary> => {
    const response = await apiClient.get<ErrorSummary>("/errors/summary", {
      params: { tenantId },
    });
    return response.data;
  },

  getTenantErrorTrend: async (
    tenantId: string,
    days = 14,
  ): Promise<ErrorTrendBucket[]> => {
    const response = await apiClient.get<ErrorTrendBucket[]>("/errors/trend", {
      params: { tenantId, days },
    });
    return response.data;
  },

  // Cross-tenant error methods (for Error Tracking page)
  getErrors: async (params?: {
    tenantId?: string;
    status?: string;
    platform?: string;
    errorType?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ data: ErrorGroup[]; total: number }> => {
    const response = await apiClient.get<{ data: ErrorGroup[]; total: number }>(
      "/errors",
      { params },
    );
    return response.data;
  },

  getErrorSummary: async (tenantId?: string): Promise<ErrorSummary> => {
    const response = await apiClient.get<ErrorSummary>("/errors/summary", {
      params: tenantId ? { tenantId } : {},
    });
    return response.data;
  },

  getErrorTrend: async (
    days = 14,
    tenantId?: string,
  ): Promise<ErrorTrendBucket[]> => {
    const response = await apiClient.get<ErrorTrendBucket[]>("/errors/trend", {
      params: { days, ...(tenantId ? { tenantId } : {}) },
    });
    return response.data;
  },

  resolveError: async (id: string, note?: string): Promise<ErrorGroup> => {
    const response = await apiClient.patch<ErrorGroup>(
      `/errors/${id}/resolve`,
      { resolvedNote: note },
    );
    return response.data;
  },

  ignoreError: async (id: string): Promise<ErrorGroup> => {
    const response = await apiClient.patch<ErrorGroup>(`/errors/${id}/ignore`);
    return response.data;
  },

  reopenError: async (id: string): Promise<ErrorGroup> => {
    const response = await apiClient.patch<ErrorGroup>(`/errors/${id}/reopen`);
    return response.data;
  },

  bulkResolveErrors: async (
    ids: string[],
    note?: string,
  ): Promise<{ resolved: number }> => {
    const response = await apiClient.post<{ resolved: number }>(
      "/errors/bulk-resolve",
      { ids, resolvedNote: note },
    );
    return response.data;
  },

  // Analytics endpoints
  getUsageTrends: async (days = 30) => {
    const response = await apiClient.get("/analytics/usage-trends", {
      params: { days },
    });
    return response.data;
  },

  getPlatformDistribution: async (days = 30) => {
    const response = await apiClient.get("/analytics/platform-distribution", {
      params: { days },
    });
    return response.data;
  },

  getTopFeatures: async (days = 30, limit = 20) => {
    const response = await apiClient.get("/analytics/top-features", {
      params: { days, limit },
    });
    return response.data;
  },

  getCohortAnalysis: async (months = 6) => {
    const response = await apiClient.get("/analytics/cohort-analysis", {
      params: { months },
    });
    return response.data;
  },

  getEngagement: async () => {
    const response = await apiClient.get("/analytics/engagement");
    return response.data;
  },

  // Segmentation endpoints
  getSegmentDistribution: async () => {
    const response = await apiClient.get("/segmentation/distribution");
    return response.data;
  },

  getSegmentDefinitions: async () => {
    const response = await apiClient.get("/segmentation/definitions");
    return response.data;
  },

  getTenantsBySegment: async (segment: string, page = 1, limit = 20) => {
    const response = await apiClient.get(`/segmentation/segment/${segment}`, {
      params: { page, limit },
    });
    return response.data;
  },

  // Lifecycle endpoints
  getLifecycleOverview: async () => {
    const response = await apiClient.get("/lifecycle/overview");
    return response.data;
  },

  getTenantLifecycle: async (tenantId: string) => {
    const response = await apiClient.get(`/lifecycle/${tenantId}`);
    return response.data;
  },

  setTenantLifecycleStage: async (
    tenantId: string,
    stage: string,
    reason: string,
  ) => {
    const response = await apiClient.patch(`/lifecycle/${tenantId}/stage`, {
      stage,
      reason,
    });
    return response.data;
  },

  // ── Bulk Operations ──────────────────────────────────────
  getBulkOperations: async (page = 1, limit = 20) => {
    const response = await apiClient.get("/bulk-operations", {
      params: { page, limit },
    });
    return response.data;
  },

  getBulkOperation: async (id: string) => {
    const response = await apiClient.get(`/bulk-operations/${id}`);
    return response.data;
  },

  previewBulkOperation: async (
    type: string,
    tenantIds: string[],
    params: Record<string, unknown>,
  ) => {
    const response = await apiClient.post("/bulk-operations/preview", {
      type,
      tenantIds,
      params,
    });
    return response.data;
  },

  bulkStatusUpdate: async (
    tenantIds: string[],
    status: string,
    control: { reason: string; dryRun?: boolean; canarySize?: number },
  ) => {
    const response = await apiClient.post("/bulk-operations/status-update", {
      tenantIds,
      status,
      ...control,
    });
    return response.data;
  },

  bulkPlanMigration: async (
    tenantIds: string[],
    planId: string,
    control: { reason: string; dryRun?: boolean; canarySize?: number },
  ) => {
    const response = await apiClient.post("/bulk-operations/plan-migration", {
      tenantIds,
      planId,
      ...control,
    });
    return response.data;
  },

  bulkModuleToggle: async (
    tenantIds: string[],
    moduleId: string,
    enabled: boolean,
    control: { reason: string; dryRun?: boolean; canarySize?: number },
  ) => {
    const response = await apiClient.post("/bulk-operations/module-toggle", {
      tenantIds,
      moduleId,
      enabled,
      ...control,
    });
    return response.data;
  },

  bulkNotification: async (
    tenantIds: string[],
    title: string,
    message: string,
    control: { reason: string; dryRun?: boolean; canarySize?: number },
  ) => {
    const response = await apiClient.post("/bulk-operations/notification", {
      tenantIds,
      title,
      message,
      ...control,
    });
    return response.data;
  },

  bulkForceLogout: async (
    tenantIds: string[],
    control: { reason: string; dryRun?: boolean; canarySize?: number },
  ) => {
    const response = await apiClient.post("/bulk-operations/force-logout", {
      tenantIds,
      ...control,
    });
    return response.data;
  },

  cancelBulkOperation: async (id: string) => {
    const response = await apiClient.post(`/bulk-operations/${id}/cancel`);
    return response.data;
  },

  executeBulkOperation: async (id: string) => {
    const response = await apiClient.post(`/bulk-operations/${id}/execute`);
    return response.data;
  },

  exportBulkOperationResults: async (id: string) => {
    const response = await apiClient.get(
      `/bulk-operations/${id}/results/export`,
    );
    return response.data as {
      filename: string;
      contentType: string;
      content: string;
    };
  },

  // ── Alert Stats / Active ─────────────────────────────────
  getActiveAlerts: async () => {
    const response = await apiClient.get("/alerts/active");
    return response.data;
  },

  getAlertStats: async () => {
    const response = await apiClient.get("/alerts/stats");
    return response.data;
  },

  getAlertDetail: async (id: string) => {
    const response = await apiClient.get(`/alerts/history/${id}`);
    return response.data;
  },

  toggleAlertRule: async (ruleId: string) => {
    const response = await apiClient.post(`/alerts/rules/${ruleId}/toggle`);
    return response.data;
  },

  // ── Storage Overview (cross-tenant) ──────────────────────
  getStorageOverview: async () => {
    const response = await apiClient.get("/monitoring/storage/overview");
    return response.data;
  },

  getStorageGrid: async (page = 1, limit = 50) => {
    const response = await apiClient.get("/monitoring/storage/grid", {
      params: { page, limit },
    });
    return response.data;
  },

  triggerStorageAudit: async () => {
    const response = await apiClient.post("/monitoring/storage/audit");
    return response.data;
  },

  // ── Error Detail (by fingerprint) ────────────────────────
  getErrorDetail: async (fingerprint: string) => {
    const response = await apiClient.get(`/errors/fingerprint/${fingerprint}`);
    return response.data;
  },

  // ── Communication Templates ──────────────────────────────
  getTemplates: async (params?: {
    channel?: string;
    category?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiClient.get("/communication-templates", {
      params,
    });
    return response.data;
  },

  getTemplate: async (id: string) => {
    const response = await apiClient.get(`/communication-templates/${id}`);
    return response.data;
  },

  createTemplate: async (data: {
    name: string;
    channel: string;
    subject?: string;
    body: string;
    variables?: string[];
    category: string;
  }) => {
    const response = await apiClient.post("/communication-templates", data);
    return response.data;
  },

  updateTemplate: async (id: string, data: Record<string, unknown>) => {
    const response = await apiClient.patch(
      `/communication-templates/${id}`,
      data,
    );
    return response.data;
  },

  deleteTemplate: async (id: string) => {
    const response = await apiClient.delete(`/communication-templates/${id}`);
    return response.data;
  },

  previewTemplate: async (
    body: string,
    subject?: string,
    variables?: string[],
  ) => {
    const response = await apiClient.post("/communication-templates/preview", {
      body,
      subject,
      variables,
    });
    return response.data;
  },

  // ── Sync Health ──────────────────────────────────────────
  getSyncOverview: async () => {
    const response = await apiClient.get("/sync-health/overview");
    return response.data;
  },

  getSyncGrid: async (page = 1, limit = 50) => {
    const response = await apiClient.get("/sync-health/grid", {
      params: { page, limit },
    });
    return response.data;
  },

  getTenantSyncHealth: async (tenantId: string, hours = 24) => {
    const response = await apiClient.get(`/sync-health/tenant/${tenantId}`, {
      params: { hours },
    });
    return response.data;
  },

  getSyncConflicts: async (params?: {
    tenantId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiClient.get("/sync-health/conflicts", {
      params,
    });
    return response.data;
  },

  resolveSyncConflict: async (id: string, strategy: string, notes?: string) => {
    const response = await apiClient.patch(
      `/sync-health/conflicts/${id}/resolve`,
      { strategy, notes },
    );
    return response.data;
  },

  // ── SLA & Performance ────────────────────────────────────
  getSlaOverview: async (hours = 24) => {
    const response = await apiClient.get("/sla/overview", {
      params: { hours },
    });
    return response.data;
  },

  getTenantPerformance: async (tenantId: string, hours = 24) => {
    const response = await apiClient.get(`/sla/tenant/${tenantId}`, {
      params: { hours },
    });
    return response.data;
  },

  getLatencyRanking: async (hours = 24, limit = 20) => {
    const response = await apiClient.get("/sla/ranking", {
      params: { hours, limit },
    });
    return response.data;
  },
};
